import React, { useState, useRef, useEffect } from 'react';
import { useCurrency, CurrencyCode } from '../../context/CurrencyContext';

interface CurrencySelectorProps {
  variant?: 'minimal' | 'dropdown';
  className?: string;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({ 
  variant = 'dropdown',
  className = ''
}) => {
  const { currentCurrency, setCurrentCurrency, availableCurrencies } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle currency selection
  const handleCurrencySelect = (currencyCode: CurrencyCode) => {
    setCurrentCurrency(currencyCode);
    setIsOpen(false);
  };

  // Render minimal variant (just the currency symbol/code)
  if (variant === 'minimal') {
    return (
      <div className={`text-sm font-medium ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-dark-lightest transition-colors"
        >
          <span>{currentCurrency.code}</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute z-50 mt-1 bg-white dark:bg-dark-lighter shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-dark-lightest">
            {availableCurrencies.map((currency) => (
              <button
                key={currency.code}
                onClick={() => handleCurrencySelect(currency.code)}
                className={`block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-dark-light transition-colors ${
                  currentCurrency.code === currency.code ? 'bg-primary-light dark:bg-primary/20 text-primary' : ''
                }`}
              >
                {currency.code} ({currency.symbol})
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Render full dropdown variant
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between space-x-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-lightest bg-white dark:bg-dark-lighter hover:border-gray-400 dark:hover:border-dark-light transition-colors w-full"
      >
        <div className="flex items-center space-x-2">
          <span className="font-medium">{currentCurrency.symbol}</span>
          <span>{currentCurrency.name}</span>
        </div>
        <svg className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-dark-lighter shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-dark-lightest">
          {availableCurrencies.map((currency) => (
            <button
              key={currency.code}
              onClick={() => handleCurrencySelect(currency.code)}
              className={`block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-dark-light transition-colors ${
                currentCurrency.code === currency.code ? 'bg-primary-light dark:bg-primary/20 text-primary' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{currency.symbol}</span>
                  <span>{currency.name}</span>
                </div>
                {currentCurrency.code === currency.code && (
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
